// ============================================
// ROLE-BASED ACCESS TYPES
// ============================================

export enum UserRole {
  PARENT = 'PARENT',
  ADMIN = 'ADMIN',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER',
  PREFER_NOT_TO_SAY = 'PREFER_NOT_TO_SAY',
}

export enum RiskLevel {
  LOW = 'LOW',
  MID = 'MID',
  HIGH = 'HIGH',
}

export enum AssessmentStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ANALYZED = 'ANALYZED',
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  YES_NO = 'YES_NO',
  RATING_SCALE = 'RATING_SCALE',
  FREE_TEXT = 'FREE_TEXT',
  NUMBER = 'NUMBER',
}

export enum QuestionnaireCategory {
  SPEECH_SOUND = 'SPEECH_SOUND',
  LANGUAGE_COMPREHENSION = 'LANGUAGE_COMPREHENSION',
  EXPRESSIVE_LANGUAGE = 'EXPRESSIVE_LANGUAGE',
  BEHAVIORAL = 'BEHAVIORAL',
}

// ============================================
// USER & AUTHENTICATION
// ============================================

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: UserRole
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// ============================================
// CHILD PROFILE
// ============================================

export interface Child {
  id: string
  userId: string
  firstName: string
  lastName?: string
  dateOfBirth: string
  gender: Gender
  ageInMonths: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  assessments?: Assessment[]
  lastAssessment?: Assessment
}

// ============================================
// QUESTIONNAIRE & QUESTIONS
// ============================================

export interface Questionnaire {
  id: string
  title: string
  description: string
  minAge: number
  maxAge: number
  version: string
  instructions?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  questions?: Question[]
  summary?: {
    totalQuestions?: number
    fitQuestionsCount?: number
    ageRange?: {
      minMonths?: number
      maxMonths?: number
      minYears?: number
      maxYears?: number
    }
    childAge?: {
      months?: number
      years?: number
      ageBand?: string
      ageYearsFloor?: number
    }
  }
}

export interface Question {
  id: string
  questionnaireId: string
  questionText: string
  questionTextEnglish?: string
  questionType: QuestionType
  category: QuestionnaireCategory
  order: number
  options?: string[]
  ageYearsVisible?: number[]
  isRequired: boolean
  createdAt: string
}

// ============================================
// ASSESSMENT & RESPONSES
// ============================================

export interface Assessment {
  id: string
  childId: string
  questionnaireId: string
  userId: string
  status: AssessmentStatus
  startedAt: string
  completedAt?: string
  createdAt: string
  updatedAt: string
  child?: Child
  questionnaire?: Questionnaire
  responses?: Response[]
  prediction?: Prediction
  progress?: number
  /** Present on list payloads from GET /assessments */
  _count?: { responses: number }
}

export interface Response {
  id: string
  assessmentId: string
  questionId: string
  answer: string
  timestamp: string
  question?: Question
}

// ============================================
// PREDICTION & RESULTS
// ============================================

export interface Prediction {
  id: string
  assessmentId: string
  riskLevel: RiskLevel
  confidenceScore: number
  recommendations: any
  domainScores?: any[] | Record<string, number>
  categoryScores?: { [key: string]: any }
  featureImportance?: { [feature: string]: number }
  modelVersion?: string
  createdAt: string
}

export interface Recommendation {
  category: string
  text: string
  priority: 'high' | 'medium' | 'low'
}

export interface DomainScore {
  domain: QuestionnaireCategory
  score: number
  maxScore: number
  percentage: number
  interpretation: string
}

// ============================================
// DASHBOARD DATA STRUCTURES
// ============================================

// Parent Dashboard
export interface ParentDashboardData {
  user: User
  children: Child[]
  recentAssessments: Assessment[]
  statistics: {
    totalChildren: number
    totalAssessments: number
    lastAssessmentDate?: string
  }
}

// Admin Dashboard
export interface AdminDashboardData {
  user: User
  statistics: {
    totalUsers: number
    totalChildren: number
    totalAssessments: number
    assessmentsToday: number
    assessmentsThisWeek: number
    assessmentsThisMonth: number
    riskDistribution: {
      low: number
      mid: number
      high: number
    }
  }
  recentAssessments: Assessment[]
  questionnaires: Questionnaire[]
}

// ============================================
// REPORT STRUCTURES
// ============================================

export interface AssessmentReport {
  assessment: Assessment
  child: Child
  prediction: Prediction
  domainAnalysis: DomainScore[]
  recommendations: Recommendation[]
  generatedAt: string
}

// ============================================
// CHART DATA
// ============================================

export interface ChartData {
  labels: string[]
  datasets: {
    label: string
    data: number[]
    backgroundColor?: string[]
    borderColor?: string[]
    borderWidth?: number
  }[]
}

// ============================================
// FORM DATA
// ============================================

export interface ChildFormData {
  firstName: string
  lastName?: string
  dateOfBirth: string
  gender: Gender
}

export interface QuestionnaireFormData {
  title: string
  description: string
  minAge: number
  maxAge: number
  instructions?: string
}

export interface QuestionFormData {
  questionText: string
  questionTextEnglish?: string
  questionType: QuestionType
  category: QuestionnaireCategory
  order: number
  options?: string[]
  isRequired: boolean
}
